import { BULK_UPLOAD_RESULT_STATUS, BulkUploadResultStatus } from "../constants/constants";
import { getFormattedDate } from "./date-formatter";

/** What "no value" looks like across the uploads table and the detail sidebar. */
export const EMPTY_VALUE = "—";

/** Label the Result badge carries. `PartiallySucceeded` is too long for a table cell. */
export const PARTIAL_RESULT_LABEL = "Partial";

/**
 * Formats a timestamp, falling back to a placeholder rather than rendering a partially formatted
 * invalid date if the API ever sends a value we cannot parse — or omits it entirely.
 */
export const formatTimestamp = (date?: Date): string => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return EMPTY_VALUE;
    }

    return getFormattedDate({ date }) ?? EMPTY_VALUE;
};

/** Counts are absent until the job records them, and zero is a real value worth showing. */
export const formatCount = (count?: number): string => (typeof count === "number" ? `${count}` : EMPTY_VALUE);

/**
 * A count against its total, as "180 / 500".
 *
 * Either side can be missing. A known total with an unrecorded count renders as "— / 500" rather than
 * collapsing to the total, which under either of this app's labels — "Processed" or "Success" — would
 * claim every row got there.
 */
export const formatProgress = (count?: number, total?: number): string => {
    const hasCount = typeof count === "number";
    const hasTotal = typeof total === "number";

    if (!hasCount && !hasTotal) {
        return EMPTY_VALUE;
    }

    if (!hasTotal) {
        return `${count}`;
    }

    return `${hasCount ? count : EMPTY_VALUE} / ${total}`;
};

/**
 * Badge colour for a processing outcome.
 *
 * Axiom has no `warning` badge, so a partial result takes `draft` — amber, and the only modifier
 * sitting between green `live` and red `bad-news`.
 */
export const getResultBadgeColor = (resultStatus: BulkUploadResultStatus): "live" | "draft" | "bad-news" => {
    switch (resultStatus) {
        case BULK_UPLOAD_RESULT_STATUS.SUCCEEDED:
            return "live";
        case BULK_UPLOAD_RESULT_STATUS.PARTIALLY_SUCCEEDED:
            return "draft";
        default:
            return "bad-news";
    }
};

/** Badge text for a processing outcome; the wire value is used as-is unless it is too long. */
export const getResultBadgeLabel = (resultStatus: BulkUploadResultStatus): string =>
    resultStatus === BULK_UPLOAD_RESULT_STATUS.PARTIALLY_SUCCEEDED ? PARTIAL_RESULT_LABEL : resultStatus;

/**
 * Prose spelling of a processing outcome, for the detail banner where there is room for the full
 * words. Only `PartiallySucceeded` is a compound; the rest are already single words, so they pass
 * through. An unrecognised value passes through too — a raw wire string tells an admin more than a
 * blank would.
 */
export const formatResultStatus = (resultStatus: string): string =>
    resultStatus === BULK_UPLOAD_RESULT_STATUS.PARTIALLY_SUCCEEDED ? "Partially Succeeded" : resultStatus;
